import React, { FC, useEffect, useState } from "react";
import { buttonVariant } from "components/atoms/Button";
import { Cross } from "components/atoms/svgs/Cross";
import { ManagePreferences } from "./components/ManagePreferences";
import { useCookiePreferences, GTMPreferences } from "hooks/useCookiePreferences";
import {
  StyledContainer,
  StyledCookie,
  FlexCol,
  StyledButtonsContainer,
  StyledButton,
  StyledCloseBtn,
  StyledText,
} from "./styles";
import { CookiePreferences } from "./types";
import { convertNewlinesToBreaks, useLocale } from "@hooks";
import { useLocaleData } from "hooks/useLocaleData";
import data from "./translations";

type Preference = keyof CookiePreferences;

interface Props {
  onSave: () => void;
  openOnModal: boolean;
}

const CookiesBar: FC<Props> = ({ onSave, openOnModal = false }) => {
  const [showModal, setShowModal] = useState(false);
  const { locale } = useLocale();

  const { cookiePreferences, updateCookiePreferences } = useCookiePreferences();

  const [preferences, setPreferences] = useState<Map<Preference, boolean>>(
    getInitialPrefs(cookiePreferences),
  );

  const { cookiesPopup } = useLocaleData(data);

  useEffect(() => {
    if (openOnModal) {
      setShowModal(true);
    }
  }, [openOnModal]);

  useEffect(() => {
    setPreferences(getInitialPrefs(cookiePreferences));
  }, [cookiePreferences]);

  const onPreferencesChange = (preference: Preference, isEnabled: boolean) => {
    const nextPrefs = new Map<Preference, boolean>([...preferences]);
    nextPrefs.set(preference, isEnabled);
    setPreferences(nextPrefs);
  };

  const updatePreferences = (variant: "accept" | "reject" | "update") => {
    if (variant === "accept") {
      updateCookiePreferences({
        ad_storage: "granted",
        analytics_storage: "granted",
        personalization_storage: "granted",
        security_storage: "granted",
      });
    }

    if (variant === "reject") {
      updateCookiePreferences({
        ad_storage: "denied",
        analytics_storage: "denied",
        personalization_storage: "denied",
        security_storage: "denied",
      });
    }

    if (variant === "update") {
      updateCookiePreferences({
        ad_storage: preferences.get("advertising") ? "granted" : "denied",
        analytics_storage: preferences.get("analytics") ? "granted" : "denied",
        personalization_storage: preferences.get("personalisation") ? "granted" : "denied",
        security_storage: preferences.get("security") ? "granted" : "denied",
      });
    }
    setShowModal(false);
    setTimeout(onSave, 0);
  };

  return (
    <StyledContainer locale={locale}>
      {!openOnModal && (
        <>
          <StyledCookie height="60" width="85" />
          <FlexCol spacing="1rem">
            <StyledText locale={locale}>{convertNewlinesToBreaks(cookiesPopup.message)}</StyledText>
            <StyledButtonsContainer>
              <StyledButton minwidth="0" onClick={() => updatePreferences("accept")}>
                {cookiesPopup.accept}
              </StyledButton>
              <StyledButton variant={buttonVariant.text} onClick={() => setShowModal(true)}>
                {cookiesPopup.manage}
              </StyledButton>
            </StyledButtonsContainer>
          </FlexCol>
          <StyledCloseBtn onClick={onSave}>
            <Cross />
          </StyledCloseBtn>
        </>
      )}
      <ManagePreferences
        showModal={showModal}
        onClose={() => setShowModal(false)}
        preferences={preferences}
        onPreferencesUpdate={onPreferencesChange}
        onAcceptAll={() => updatePreferences("accept")}
        onRejectAll={() => updatePreferences("reject")}
        onSaveSettings={() => updatePreferences("update")}
      />
    </StyledContainer>
  );
};

export default CookiesBar;

const getInitialPrefs = (prefs: GTMPreferences) => {
  return new Map<Preference, boolean>([
    ["analytics", prefs.analytics_storage === "granted"],
    ["advertising", prefs.ad_storage === "granted"],
    ["personalisation", prefs.personalization_storage === "granted"],
    ["security", prefs.security_storage === "granted"],
  ]);
};
